import { Radio, RadioOption } from "components/shared/radio";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import {
    RegionVisibility,
    RegionVisibilityTree,
} from "components/ui/region-visibility-tree";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    ActionType,
    AdvancedSearchEntityType,
    CreateWorkflowMutation,
    CustomListKey,
    useCreateWorkflowMutation,
    WorkflowPayload,
} from "generated/graphql";
import React, { useEffect, useMemo, useState } from "react";
import { useCustomList, useWorkflowEntityTypes } from "store/selectors/bridge";
import { useCurrentUserId, useDefaultRegionVisibility } from "store/selectors/hooks";
import { entityTypeLabel } from "./manage-workflows-helper";
import { WorkflowTemplateKey, workflowTemplates } from "./workflow-template";

interface CreateWorkflowPanelProps {
    onSaved: (workflow: CreateWorkflowMutation["createWorkflow"]) => void;
    onCancel: () => void;
}

export const CreateWorkflowPanel: React.FC<CreateWorkflowPanelProps> = props => {
    const { onSaved, onCancel } = props;

    const [selectedEntityType, setSelectedEntityType] =
        useState<AdvancedSearchEntityType | null>(null);
    const [selectedTemplate, setSelectedTemplate] =
        React.useState<WorkflowTemplateKey | null>(null);

    const [workflowName, setWorkflowName] = React.useState<string>("");
    const [workflowDescription, setWorkflowDescription] = React.useState<string>("");
    const currentUserId = useCurrentUserId();

    const createWorkflowMutation = useCreateWorkflowMutation();
    const { customList } = useCustomList(CustomListKey.ClientStatus);
    const { customList: homeCareLeadStatusCustomList } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );

    const workflowEntityTypes = useWorkflowEntityTypes();

    const defaultRegionVisibility = useDefaultRegionVisibility();
    const [regionVisibility, setRegionVisibility] = useState<RegionVisibility[]>(
        defaultRegionVisibility,
    );

    useEffect(() => {
        if (workflowEntityTypes.length === 1) {
            setSelectedEntityType(workflowEntityTypes[0]);
        }
    }, [workflowEntityTypes]);

    const entityTypeOptions: RadioOption[] = useMemo(() => {
        return workflowEntityTypes.map(et => ({
            key: et,
            text: entityTypeLabel(et),
        }));
    }, [workflowEntityTypes]);

    const workflowTemplateOptions: RadioOption[] = useMemo(() => {
        if (!selectedEntityType) return [];
        return Object.values(workflowTemplates)
            .filter(template => template.entityType === selectedEntityType)
            .map(template => ({
                key: template.key,
                text: template.text,
                helpText: template.helpText,
            }));
    }, [selectedEntityType]);

    useEffect(() => {
        if (
            workflowTemplateOptions.length > 0 &&
            (selectedTemplate === null ||
                !workflowTemplateOptions.some(o => o.key === selectedTemplate))
        ) {
            setSelectedTemplate(workflowTemplateOptions[0].key as WorkflowTemplateKey);
        }
    }, [workflowTemplateOptions, selectedTemplate]);

    const workflowPayload: WorkflowPayload = useMemo(() => {
        if (!customList || !selectedTemplate) {
            return null;
        }

        return workflowTemplates[selectedTemplate].toWorkflow({
            name: workflowName,
            description: workflowDescription,
            currentUserId,
            clientStatusCustomList: customList,
            homeCareLeadStatusCustomList: homeCareLeadStatusCustomList ?? null,
            regionVisibility,
        });
    }, [
        selectedTemplate,
        workflowName,
        workflowDescription,
        currentUserId,
        customList,
        homeCareLeadStatusCustomList,
        regionVisibility,
    ]);

    useEffect(() => {
        if (!selectedTemplate) {
            return;
        }
        const newTemplate = workflowTemplates[selectedTemplate];
        setWorkflowName(newTemplate.text);
        setWorkflowDescription(newTemplate.helpText);
    }, [selectedTemplate]);

    const handleCreate = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const result = await createWorkflowMutation.mutateAsync({
                workflow: workflowPayload,
            });
            onSaved(result.createWorkflow);
        },
        [createWorkflowMutation, onSaved, workflowPayload],
    );

    const handleCancel = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onCancel();
        },
        [onCancel],
    );

    const handleBack = React.useCallback(() => {
        setSelectedEntityType(null);
        setSelectedTemplate(null);
    }, []);

    if (workflowEntityTypes.length > 1 && !selectedEntityType) {
        return (
            <div className="space-y-4">
                <p>Choose what type of workflow to create.</p>
                <Radio
                    onChange={val =>
                        setSelectedEntityType(val as AdvancedSearchEntityType)
                    }
                    options={entityTypeOptions}
                    value=""
                    label="Workflow type"
                />
                <div className="flex items-center space-x-2">
                    <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
                </div>
            </div>
        );
    }

    if (!workflowPayload) {
        return null;
    }

    return (
        <div className="space-y-4">
            <p>
                Create a workflow to trigger automation when certain criteria are met.
                Choose a template, configure basic settings, then save.
            </p>
            <p>
                After you create this workflow, it will be in a draft state so you can
                finalize any settings before publishing it.
            </p>
            {workflowEntityTypes.length > 1 ? (
                <SecondaryButton onClick={handleBack}>
                    &larr; Back to workflow type
                </SecondaryButton>
            ) : null}
            <Radio
                onChange={val => setSelectedTemplate(val as WorkflowTemplateKey)}
                options={workflowTemplateOptions}
                value={selectedTemplate}
                label="Template"
            />
            <Input
                label="Name"
                value={workflowName}
                onChange={e => setWorkflowName(e.target.value)}
            />
            <Input
                label="Description"
                value={workflowDescription}
                onChange={e => setWorkflowDescription(e.target.value)}
            />
            <RegionVisibilityTree
                actionType={ActionType.CreateWorkflow}
                editable={true}
                regionVisibility={regionVisibility}
                onChange={setRegionVisibility}
                entityName="Workflow"
            />
            <div className="flex items-center space-x-2">
                <PrimaryButton
                    onClick={handleCreate}
                    disabled={regionVisibility.length === 0}>
                    Create workflow
                </PrimaryButton>
                <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
            </div>
            {createWorkflowMutation.isLoading ? <Spinner /> : null}
        </div>
    );
};
